/**
 * NoteEditorModalView - Modal form for adding and editing a player note
 *
 * SE-5519: replaces the inline "+ Add" compose form and the per-note inline
 * edit form with a single modal editor styled after Figma node 2004:346
 * (dark note-modal). Reuses the shared FullScreenOverlay shell (which owns the
 * card surface, header title, close affordance, and the scrollable body) so the
 * editor inherits the app's modal chrome instead of re-deriving token-snapped
 * card/shadow/radius values (see .lisa/se5519/token-mapping.md §D 1/2/9/10 —
 * those literals belong to the shell, not this form).
 *
 * The component is fully controlled: all note state (input text, visibility,
 * submit/create-then-delete semantics) lives in NotesSectionContainer. Like
 * NoteItem in this directory, it is View-only — there is no per-modal hook state
 * to own, so a Container passthrough would be empty ceremony. Per-open reset is
 * handled by the parent keying this element on mode/noteId.
 *
 * The visibility toggle renders only in add mode, matching the pre-redesign
 * behavior where the inline edit form never exposed it (an edited note keeps its
 * original visibility, seeded into `isPublic` by the container).
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React, { memo } from "react";
import { Platform } from "react-native";

import FullScreenOverlay from "@/components/organisms/shared/FullScreenOverlay";
import { Button } from "@/components/atoms/Button";
import { KeyboardAvoidingView } from "@/components/atoms/KeyboardAvoidingView";
import { Stack } from "@/components/atoms/Stack";
import { Switch } from "@/components/atoms/Switch";
import { Text } from "@/components/atoms/Text";
import { Textarea, TextareaInput } from "@/components/atoms/Textarea";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/** Editor mode — add a new note or edit an existing one. */
export type NoteEditorMode = "add" | "edit";

/** Header title per mode (Figma "Scouting Note" surfaced as an add/edit action). */
const MODE_TITLE: Record<NoteEditorMode, string> = {
  add: "Add Note",
  edit: "Edit Note",
};

/**
 * Props for NoteEditorModalView component
 */
interface NoteEditorModalViewProps {
  /** Whether the editor modal is visible */
  readonly isOpen: boolean;
  /** Whether the editor is adding a new note or editing an existing one */
  readonly mode: NoteEditorMode;
  /** Current note body text */
  readonly noteInput: string;
  /** Whether the note is visible to others (public) */
  readonly isPublic: boolean;
  /** Callback when the note text changes */
  readonly onNoteInputChange: (text: string) => void;
  /** Callback when the visibility toggle changes */
  readonly onToggleVisibility: (value: boolean) => void;
  /** Callback when the note is submitted (Save) */
  readonly onSubmit: () => void;
  /** Callback to close/cancel the editor */
  readonly onClose: () => void;
}

/**
 * NoteEditorModalView renders the add/edit note modal form
 * @param props - View props
 * @param props.isOpen - Whether the editor modal is visible
 * @param props.mode - Whether the editor is adding or editing
 * @param props.noteInput - Current note body text
 * @param props.isPublic - Whether the note is visible to others
 * @param props.onNoteInputChange - Callback when the note text changes
 * @param props.onToggleVisibility - Callback when the visibility toggle changes
 * @param props.onSubmit - Callback when the note is submitted (Save)
 * @param props.onClose - Callback to close/cancel the editor
 * @returns FullScreenOverlay hosting the note editor form
 */
const NoteEditorModalView = ({
  isOpen,
  mode,
  noteInput,
  isPublic,
  onNoteInputChange,
  onToggleVisibility,
  onSubmit,
  onClose,
}: NoteEditorModalViewProps) => (
  <FullScreenOverlay
    isOpen={isOpen}
    onClose={onClose}
    title={MODE_TITLE[mode]}
    // SE-5578: the Figma note-editor card (node 4:55) is 750px wide and
    // content-hugging — the former "md" (510px cap) plus the overlay's old
    // 60vh height floor produced the reported narrow-but-tall cramped dialog.
    desktopSize="xl"
  >
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <Stack
        className="gap-4"
        testID={TEST_ID_PLAYER_DETAIL.NOTE_EDITOR_MODAL}
        {...{ "data-testid": TEST_ID_PLAYER_DETAIL.NOTE_EDITOR_MODAL }}
      >
        {/* Body — large multi-line compose area (Figma 400px scroll region → */}
        {/* a flexible min-height that scrolls inside the shell body, per §D 8) */}
        {/* SE-6600: overriding the atom's fixed base height is load-bearing. */}
        {/* The Textarea atom's base style carries a definite short height; a */}
        {/* bare minimum-height override does not replace it (different */}
        {/* tailwind-merge group), so the parent column measured this box at */}
        {/* its short base height while it laid out much taller. That made */}
        {/* the modal body far shorter than its actual content, pushing the */}
        {/* visibility toggle and the Cancel/Save row outside their parent's */}
        {/* bounds — still painted, but on Android a child outside its */}
        {/* parent's bounds receives no touches, so the toggle was dead until */}
        {/* a keyboard show/hide forced a re-layout. */}
        <Textarea size="xl" className="h-auto min-h-64">
          <TextareaInput
            testID={TEST_ID_PLAYER_DETAIL.NOTE_EDITOR_INPUT}
            value={noteInput}
            onChangeText={onNoteInputChange}
            placeholder="Write a note..."
          />
        </Textarea>

        {/* Visibility toggle — add flow only (edit keeps the note's own */}
        {/* visibility, matching the pre-redesign inline edit form) */}
        {mode === "add" && (
          <Stack
            direction="horizontal"
            className="items-center justify-between"
          >
            <Text variant="body" className="text-content-secondary">
              Visible to others?
            </Text>
            <Switch
              testID={TEST_ID_PLAYER_DETAIL.NOTE_EDITOR_VISIBILITY_SWITCH}
              value={isPublic}
              onValueChange={onToggleVisibility}
              accessibilityLabel="Visible to others?"
            />
          </Stack>
        )}

        {/* Footer actions — sticky Cancel / Save (Save disabled on empty input) */}
        <Stack
          direction="horizontal"
          className="border-border-outline-subtle justify-end gap-2 border-t pt-4"
        >
          <Button
            label="Cancel"
            tone="neutral"
            variant="outline"
            size="sm"
            onPress={onClose}
            testID={TEST_ID_PLAYER_DETAIL.NOTE_EDITOR_CANCEL_BUTTON}
          />
          <Button
            label="Save"
            tone="primary"
            size="sm"
            isDisabled={noteInput.trim().length === 0}
            onPress={onSubmit}
            testID={TEST_ID_PLAYER_DETAIL.NOTE_EDITOR_SAVE_BUTTON}
          />
        </Stack>
      </Stack>
    </KeyboardAvoidingView>
  </FullScreenOverlay>
);

NoteEditorModalView.displayName = "NoteEditorModalView";

export default memo(NoteEditorModalView);
